/**
 * PixelDocument: an immutable-by-convention RGBA pixel grid with invariant
 * validation. Width and height are each clamped to 1..128 (see limits.ts).
 * Pixels are stored row-major in a flat Uint8ClampedArray (r,g,b,a per pixel).
 * A pixel with alpha 0 is "transparent" regardless of its RGB channels.
 */

import { MAX_DIMENSION, MIN_DIMENSION } from "./limits.js";
import { PixelwerkstattError } from "./errors.js";
import { cloneRgba, isRgba, rgbaEqual, type Rgba } from "./color.js";

export const TRANSPARENT_PIXEL: Readonly<Rgba> = Object.freeze({ r: 0, g: 0, b: 0, a: 0 });

export interface PixelDocumentInit {
  width: number;
  height: number;
  /** Optional initial pixels: flat RGBA quadruples (length = width*height*4) or a PixelDocument to copy. */
  data?: Uint8ClampedArray;
}

function assertDimension(name: "width" | "height", value: number): void {
  if (typeof value !== "number" || !Number.isInteger(value)) {
    throw new PixelwerkstattError(
      "DIMENSION_TYPE",
      `${name} muss eine ganze Zahl sein (erhalten: ${String(value)}).`,
    );
  }
  if (value < MIN_DIMENSION || value > MAX_DIMENSION) {
    throw new PixelwerkstattError(
      "DIMENSION_RANGE",
      `${name} muss zwischen ${MIN_DIMENSION} und ${MAX_DIMENSION} liegen (erhalten: ${value}).`,
    );
  }
}

export class PixelDocument {
  private _width: number;
  private _height: number;
  private _data: Uint8ClampedArray;

  private constructor(width: number, height: number, data: Uint8ClampedArray) {
    this._width = width;
    this._height = height;
    this._data = data;
  }

  /** Create a new document. All pixels start transparent unless data is given. */
  static create(width: number, height: number, data?: Uint8ClampedArray): PixelDocument {
    assertDimension("width", width);
    assertDimension("height", height);
    const expected = width * height * 4;
    if (data === undefined) {
      return new PixelDocument(width, height, new Uint8ClampedArray(expected));
    }
    if (!(data instanceof Uint8ClampedArray)) {
      throw new PixelwerkstattError(
        "PIXELS_TYPE",
        "Daten müssen ein Uint8ClampedArray mit width*height*4 Werten sein.",
      );
    }
    if (data.length !== expected) {
      throw new PixelwerkstattError(
        "PIXELS_ROW_WIDTH",
        `Daten haben ${data.length} Werte, erwartet werden ${expected} (width*height*4).`,
      );
    }
    return new PixelDocument(width, height, new Uint8ClampedArray(data));
  }

  get width(): number {
    return this._width;
  }

  get height(): number {
    return this._height;
  }

  /** Total pixel count (width * height). */
  get pixelCount(): number {
    return this._width * this._height;
  }

  /** Direct read-only view on the flat RGBA data (row-major, r,g,b,a per pixel). */
  get data(): Readonly<Uint8ClampedArray> {
    return this._data;
  }

  assertIndex(x: number, y: number): void {
    if (typeof x !== "number" || !Number.isInteger(x) || typeof y !== "number" || !Number.isInteger(y)) {
      throw new PixelwerkstattError(
        "DIMENSION_TYPE",
        `Zellkoordinaten müssen ganze Zahlen sein (erhalten: x=${String(x)}, y=${String(y)}).`,
      );
    }
    if (x < 0 || y < 0 || x >= this._width || y >= this._height) {
      throw new PixelwerkstattError(
        "DIMENSION_RANGE",
        `Zelle (${x}, ${y}) liegt außerhalb des ${this._width}×${this._height}-Dokuments.`,
      );
    }
  }

  /** Read the pixel at (x, y). Returns a fresh Rgba object. */
  getPixel(x: number, y: number): Rgba {
    this.assertIndex(x, y);
    const i = (y * this._width + x) * 4;
    return { r: this._data[i]!, g: this._data[i + 1]!, b: this._data[i + 2]!, a: this._data[i + 3]! };
  }

  isTransparent(x: number, y: number): boolean {
    return this.getPixel(x, y).a === 0;
  }

  /** Write the pixel at (x, y). The color is copied; invalid colors throw. */
  setPixel(x: number, y: number, color: Rgba): void {
    this.assertIndex(x, y);
    if (!isRgba(color)) {
      throw new PixelwerkstattError(
        "COLOR_TYPE",
        `Pixel-Farbe muss ein Rgba-Objekt mit Kanälen 0–255 sein (erhalten: ${JSON.stringify(color)}).`,
      );
    }
    const i = (y * this._width + x) * 4;
    this._data[i] = color.r;
    this._data[i + 1] = color.g;
    this._data[i + 2] = color.b;
    this._data[i + 3] = color.a;
  }

  /** Set the pixel to fully transparent. */
  clearPixel(x: number, y: number): void {
    this.setPixel(x, y, TRANSPARENT_PIXEL);
  }

  /** Set every pixel to fully transparent (dimensions unchanged). */
  clearAll(): void {
    this._data.fill(0);
  }

  /**
   * Return a new document with the given dimensions. Existing pixels keep
   * their color where they still fit; new pixels are transparent.
   * Content is anchored top-left. The document itself is not mutated.
   */
  resize(width: number, height: number): PixelDocument {
    assertDimension("width", width);
    assertDimension("height", height);
    const next = PixelDocument.create(width, height);
    const copyW = Math.min(this._width, width);
    const copyH = Math.min(this._height, height);
    for (let y = 0; y < copyH; y++) {
      const srcRow = y * this._width;
      const dstRow = y * width;
      for (let x = 0; x < copyW; x++) {
        const s = (srcRow + x) * 4;
        const d = (dstRow + x) * 4;
        next._data[d] = this._data[s]!;
        next._data[d + 1] = this._data[s + 1]!;
        next._data[d + 2] = this._data[s + 2]!;
        next._data[d + 3] = this._data[s + 3]!;
      }
    }
    return next;
  }

  /** Deep copy with the same dimensions and pixel content. */
  clone(): PixelDocument {
    return new PixelDocument(this._width, this._height, new Uint8ClampedArray(this._data));
  }

  /** True when both documents have identical dimensions and pixel data. */
  equals(other: PixelDocument): boolean {
    return (
      this._width === other._width &&
      this._height === other._height &&
      this._data.length === other._data.length &&
      everyIndex(this._data.length, (i) => this._data[i] === other._data[i])
    );
  }

  /**
   * Replace all pixel data (dimensions included) from a parsed source.
   * Used by the format/history layers; input must already be validated
   * (throws PixelwerkstattError otherwise). Mutates this document in place.
   */
  replaceWith(width: number, height: number, data: Uint8ClampedArray): void {
    const doc = PixelDocument.create(width, height, data);
    this._width = doc._width;
    this._height = doc._height;
    this._data = doc._data;
  }
}

function everyIndex(length: number, predicate: (i: number) => boolean): boolean {
  for (let i = 0; i < length; i++) {
    if (!predicate(i)) return false;
  }
  return true;
}

// Re-exported for convenience of consumers of the document module.
export { rgbaEqual, cloneRgba };
